
import { useState } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';

const QuoteForm = () => {
  const { currentLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    location: '',
    description: '',
    budget: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const getTexts = () => {
    switch (currentLanguage) {
      case 'fr':
        return {
          title: 'Demandez un Devis Gratuit',
          subtitle: 'Obtenez une estimation personnalisée pour votre projet',
          name: 'Nom complet',
          email: 'Email',
          phone: 'Téléphone',
          service: 'Service requis',
          location: 'Lieu du projet',
          description: 'Description du projet',
          budget: 'Budget estimé',
          submit: 'Envoyer la demande',
          submitting: 'Envoi en cours...',
          success: 'Demande envoyée avec succès! Nous vous contacterons bientôt.',
          error: 'Erreur lors de l\'envoi. Veuillez réessayer.',
          services: {
            renovation: 'Rénovation',
            painting: 'Peinture',
            plastering: 'Plâtrerie',
            flooring: 'Revêtements de sol',
            facade: 'Travaux de façade',
            other: 'Autre'
          },
          budgets: {
            '5k': 'Moins de 5\'000 CHF',
            '5k-15k': '5\'000 - 15\'000 CHF',
            '15k-30k': '15\'000 - 30\'000 CHF',
            '30k-50k': '30\'000 - 50\'000 CHF',
            '50k+': 'Plus de 50\'000 CHF'
          }
        };
      case 'en':
        return {
          title: 'Request a Free Quote',
          subtitle: 'Get a personalized estimate for your project',
          name: 'Full name',
          email: 'Email',
          phone: 'Phone',
          service: 'Required service',
          location: 'Project location',
          description: 'Project description',
          budget: 'Estimated budget',
          submit: 'Send request',
          submitting: 'Sending...',
          success: 'Request sent successfully! We will contact you soon.',
          error: 'Error sending request. Please try again.',
          services: {
            renovation: 'Renovation',
            painting: 'Painting',
            plastering: 'Plastering',
            flooring: 'Flooring',
            facade: 'Facade work',
            other: 'Other'
          },
          budgets: {
            '5k': 'Less than 5\'000 CHF',
            '5k-15k': '5\'000 - 15\'000 CHF',
            '15k-30k': '15\'000 - 30\'000 CHF',
            '30k-50k': '30\'000 - 50\'000 CHF',
            '50k+': 'More than 50\'000 CHF'
          }
        };
      case 'ro':
        return {
          title: 'Solicitați o Ofertă Gratuită',
          subtitle: 'Obțineți o estimare personalizată pentru proiectul dvs.',
          name: 'Nume complet',
          email: 'Email',
          phone: 'Telefon',
          service: 'Serviciu necesar',
          location: 'Locația proiectului',
          description: 'Descrierea proiectului',
          budget: 'Buget estimat',
          submit: 'Trimite cererea',
          submitting: 'Se trimite...',
          success: 'Cererea a fost trimisă cu succes! Vă vom contacta în curând.',
          error: 'Eroare la trimitere. Vă rugăm să încercați din nou.',
          services: {
            renovation: 'Renovare',
            painting: 'Vopsitorie',
            plastering: 'Tencuială',
            flooring: 'Pardoseli',
            facade: 'Lucrări de fațadă',
            other: 'Altele'
          },
          budgets: {
            '5k': 'Mai puțin de 5\'000 CHF',
            '5k-15k': '5\'000 - 15\'000 CHF',
            '15k-30k': '15\'000 - 30\'000 CHF',
            '30k-50k': '30\'000 - 50\'000 CHF',
            '50k+': 'Peste 50\'000 CHF'
          }
        };
      default:
        return {
          title: 'Запросить Бесплатное Предложение',
          subtitle: 'Получите персональную оценку для вашего проекта',
          name: 'Полное имя',
          email: 'Email',
          phone: 'Телефон',
          service: 'Требуемая услуга',
          location: 'Местоположение проекта',
          description: 'Описание проекта',
          budget: 'Предполагаемый бюджет',
          submit: 'Отправить запрос',
          submitting: 'Отправка...',
          success: 'Запрос успешно отправлен! Мы свяжемся с вами в ближайшее время.',
          error: 'Ошибка при отправке. Пожалуйста, попробуйте снова.',
          services: {
            renovation: 'Ремонт',
            painting: 'Покраска',
            plastering: 'Штукатурка',
            flooring: 'Напольные покрытия',
            facade: 'Фасадные работы',
            other: 'Другое'
          },
          budgets: {
            '5k': 'Менее 5\'000 CHF',
            '5k-15k': '5\'000 - 15\'000 CHF',
            '15k-30k': '15\'000 - 30\'000 CHF',
            '30k-50k': '30\'000 - 50\'000 CHF',
            '50k+': 'Более 50\'000 CHF'
          }
        };
    }
  };

  const texts = getTexts();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3oglatna1m5mm1skbu0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          location: '',
          description: '',
          budget: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="quote" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {texts.title}
          </h2>
          <p className="text-xl text-gray-600">
            {texts.subtitle}
          </p>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8">
          <form id="quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  {texts.name} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {texts.email} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {texts.phone} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                  {texts.service} *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">{currentLanguage === 'fr' ? 'Sélectionnez un service' : currentLanguage === 'en' ? 'Select a service' : currentLanguage === 'ro' ? 'Selectați un serviciu' : 'Выберите услугу'}</option>
                  <option value="renovation">{texts.services.renovation}</option>
                  <option value="painting">{texts.services.painting}</option>
                  <option value="plastering">{texts.services.plastering}</option>
                  <option value="flooring">{texts.services.flooring}</option>
                  <option value="facade">{texts.services.facade}</option>
                  <option value="other">{texts.services.other}</option>
                </select>
              </div>

              <div>
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                  {texts.location} *
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  required
                  value={formData.location}
                  onChange={handleChange}
                  placeholder={currentLanguage === 'fr' ? 'ex: Genève, Lausanne...' : currentLanguage === 'en' ? 'e.g: Geneva, Lausanne...' : currentLanguage === 'ro' ? 'ex: Geneva, Lausanne...' : 'напр: Женева, Лозанна...'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                  {texts.budget}
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">{currentLanguage === 'fr' ? 'Sélectionnez un budget' : currentLanguage === 'en' ? 'Select a budget' : currentLanguage === 'ro' ? 'Selectați un buget' : 'Выберите бюджет'}</option>
                  <option value="5k">{texts.budgets['5k']}</option>
                  <option value="5k-15k">{texts.budgets['5k-15k']}</option>
                  <option value="15k-30k">{texts.budgets['15k-30k']}</option>
                  <option value="30k-50k">{texts.budgets['30k-50k']}</option>
                  <option value="50k+">{texts.budgets['50k+']}</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                {texts.description} *
              </label>
              <textarea
                id="description"
                name="description"
                required
                rows={4}
                maxLength={500}
                value={formData.description}
                onChange={handleChange}
                placeholder={currentLanguage === 'fr' ? 'Décrivez votre projet en détail...' : currentLanguage === 'en' ? 'Describe your project in detail...' : currentLanguage === 'ro' ? 'Descrieți proiectul în detaliu...' : 'Опишите ваш проект подробно...'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
              ></textarea>
              <p className="text-xs text-gray-500 mt-1">{formData.description.length}/500</p>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-600 text-lg mr-3"></i>
                  <p className="text-green-800">{texts.success}</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 text-lg mr-3"></i>
                  <p className="text-red-800">{texts.error}</p>
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors font-semibold whitespace-nowrap inline-flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    {texts.submitting}
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    {texts.submit}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
